/*eslint max-lines-per-function: ["error", 423]*/
/*eslint complexity: ["error", 26]*/
import React from 'react';
import * as Icon from 'react-bootstrap-icons';
import 'react-circular-progressbar/dist/styles.css';

import {
  CreateUpdateEstimateItemDtoPositionTypeEnum,
  GetExcelColumnsDto,
  GetExcelConfigDto,
  GetExcelRowDto,
  GetExcelRowDtoPositionTypeEnum,
  GetPositionExtendedDtoPositionTypeEnum,
  ParametersSchemaDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridColumn,
  GridRecord,
} from '../../../common/components/Grid/GridDataTypes';
import {
  ExcelImportStatusEnum,
  ExcelImportStatusObj,
} from '../../../common/pokCore/validation/enums';
import momencik from '../../../common/momencik';
import numberFormatter from '../../../common/numberFormatter';
import { useTranslation } from '../../../common/hooks/useTranslation';
import PopoverText from '../../../app/components/PopoverText';
import { isFieldHidden } from '../Estimates/CommissionService';
import mathUtils from '../../../utils/mathUtils';
import { EstimateItemFieldsDefinition } from '../../../common/pokCore/validation/estimateItemFields.definition';
import { usePokCore } from '../../../common/hooks/usePokCore';

import { PositionMapForm } from './PositionMapForm';
import { ParameterMapperForm } from './ParameterMapperForm';
import { PositionChangeForm } from './PositionChangeForm';
import {
  SpecialistTeamMapperForm,
  getExcelRowTeamName,
} from './SpecialistTeamMapperForm';

interface ExcelPreviewGridProps {
  data: GetExcelRowDto[];
  columns: GetExcelColumnsDto[];
  otherRequiredColumns: GetExcelColumnsDto[];
  config: GetExcelConfigDto;
  hideErrors: boolean;
  refresh?: boolean;
  updatePosition: (
    obj: Partial<GetExcelRowDto>,
    parameters?: ParametersSchemaDto,
  ) => void;
  changePosition: (
    rowId: string,
    newPositionName: string,
    newPositionId: string,
    newPositionType: GetPositionExtendedDtoPositionTypeEnum,
  ) => void;
  updateParams?: (
    rowId: string,
    key: string,
    newValue: string,
    dictValue: string,
    dictValuePl: string,
    dictValueEn: string,
  ) => void;
  updateSpecialistTeam?: (
    rowId: string,
    newSpecialistTeamId: string,
    newSpecialistTeamName: string,
  ) => void;
}

export const ExcelPreviewGrid: React.FC<ExcelPreviewGridProps> = props => {
  const { t, tk } = useTranslation('excelImportLng');
  const pok = usePokCore();
  const lang = pok.getLanguage();

  const generateErrMsg = (excelValue?: string) => {
    return (
      <>
        [{t(tk.import.lack)}]{' '}
        <PopoverText
          headerText={t(tk.import.positionNameInExcel)}
          bodyText={excelValue || '-'}
          icon={Icon.ExclamationCircle}
          className="ms-2"
        />
      </>
    );
  };

  const generateAmountFieldValue = (
    key: string,
    value?: string,
    excelValue?: string,
    positionType?: GetExcelRowDtoPositionTypeEnum,
  ) => {
    if (
      isFieldHidden(
        key,
        positionType as CreateUpdateEstimateItemDtoPositionTypeEnum,
        props.config.profile,
      )
    ) {
      return '';
    }
    return value
      ? numberFormatter(value, true)
      : excelValue
        ? generateErrMsg(excelValue)
        : '';
  };

  const generateDictionaryFieldValue = (
    rowId: string,
    key: string,
    aggregatedRows: boolean,
    dictValue?: string,
    dictValuePl?: string,
    dictValueEn?: string,
    dictKey?: string,
    excelValue?: string,
    dictName?: string,
  ) => {
    return (
      <ParameterMapperForm
        dictValue={dictValue}
        dictValuePl={dictValuePl}
        dictValueEn={dictValueEn}
        dictKey={dictKey}
        excelParameterName={excelValue}
        dictName={dictName}
        aggregatedRows={aggregatedRows}
        existingParamLabel={t(tk.import.nameFromExcel)}
        handleUpdate={(
          newDictKey,
          newDictValue,
          newDictValuePl,
          newDictValueEn,
        ) =>
          props.updateParams &&
          props.updateParams(
            rowId,
            key,
            newDictKey,
            newDictValue,
            newDictValuePl,
            newDictValueEn,
          )
        }
      />
    );
  };

  const generateSpecialistTeamFieldValue = (d: GetExcelRowDto) => {
    const positionTeamIds = d.positionAssignedSpecialistTeamIds ?? [];
    const usesSpecialistTeam = positionTeamIds.length > 0;
    if (!usesSpecialistTeam && !d.companyId) {
      return (
        getExcelRowTeamName(d) ??
        (d.excelSpecialistTeam ? generateErrMsg(d.excelSpecialistTeam) : '')
      );
    }
    return (
      <SpecialistTeamMapperForm
        specialistTeamId={
          usesSpecialistTeam ? d.specialistTeam : d.deliveryTeam
        }
        specialistTeamName={getExcelRowTeamName(d)}
        candidateTeamIds={positionTeamIds}
        companyId={usesSpecialistTeam ? undefined : d.companyId}
        aggregatedRows={d.rowId.toString().split(',').length > 1}
        handleUpdate={(newSpecialistTeamId, newSpecialistTeamName) =>
          props.updateSpecialistTeam &&
          props.updateSpecialistTeam(
            d.rowId,
            newSpecialistTeamId,
            newSpecialistTeamName,
          )
        }
      />
    );
  };

  const generatePositionField = (d: GetExcelRowDto) => {
    if (!d.excelPositionName) {
      return `[${t(tk.import.noName)}]`;
    }
    if (!d.projectNumber) {
      return `[${t(tk.import.notFound)}]`;
    }
    if (d.positionName && d.positionId) {
      return (
        <PositionChangeForm
          profile={props.config.profile}
          teamIds={props.config.excelConfigTeams?.map(t => t.team.id) || []}
          configId={props.config.id}
          positionId={d.positionId}
          positionName={d.positionName}
          aggregatedRows={d.rowId.toString().split(',').length > 1}
          handleUpdate={(newPositionId, newPositionName, newPositionType) =>
            props.changePosition &&
            props.changePosition(
              d.rowId,
              newPositionName,
              newPositionId,
              newPositionType,
            )
          }
        />
      );
    }

    return (
      <PositionMapForm
        excelPositionName={d.excelPositionName}
        profile={props.config.profile}
        teams={props.config.excelConfigTeams?.map(t => t.team.id) || []}
        configId={props.config.id}
        handleUpdate={(positionName, positionType, positionId, parameters) =>
          props.updatePosition &&
          props.updatePosition(
            {
              positionName: positionName,
              positionType: positionType,
              positionId: positionId,
              excelPositionName: d.excelPositionName,
            },
            parameters,
          )
        }
      />
    );
  };

  const getOrderByNumberColumns = (d: GetExcelRowDto, keys: string[]) => {
    const columns: Record<string, number> = {};

    for (const key of keys) {
      let value = d[key as keyof GetExcelRowDto] ?? '-9999999';
      if (
        isFieldHidden(
          key,
          d.positionType as CreateUpdateEstimateItemDtoPositionTypeEnum,
          props.config.profile,
        )
      ) {
        value = '-9999999';
      }
      if (typeof value === 'string') {
        columns[key] = mathUtils.parse(value);
      }
    }
    return columns;
  };

  const generateErrorsInfo = (d: GetExcelRowDto) => {
    const errRow = [];
    if (d.positionNotFoundError) {
      errRow.push(
        <div key={d.rowId}>
          <b>{d.positionNotFoundError}</b>
        </div>,
      );
    }
    errRow.push(
      ...d.otherErrors
        .concat(d.parameterErrors)
        .concat(d.calculationErrors)
        .map((e, i) => <div key={i}>{e}</div>),
    );

    return errRow;
  };

  const generateAmountFields = (d: GetExcelRowDto) => {
    const columns: Record<string, React.ReactNode> = {};
    for (const key of EstimateItemFieldsDefinition.amountFieldsKeys) {
      const value =
        (d[key.name as keyof GetExcelRowDto] as string) ?? undefined;
      const excelKeyName = `excel${key.name.charAt(0).toUpperCase()}${key.name.slice(
        1,
      )}`;
      const excelValue =
        (d[excelKeyName as keyof GetExcelRowDto] as string) ?? undefined;
      columns[key.name] = isHidden(key.name, d)
        ? ''
        : generateAmountFieldValue(key.name, value, excelValue, d.positionType);
    }
    return columns;
  };

  const generatePercentageFields = (d: GetExcelRowDto) => {
    const columns: Record<string, React.ReactNode> = {};
    for (const key of EstimateItemFieldsDefinition.percentageFieldsKeys) {
      const value =
        (d[key.name as keyof GetExcelRowDto] as string) ?? undefined;
      let percentageValue = '';
      if (value) {
        percentageValue = isNaN(Number(value))
          ? value
          : `${mathUtils.parse(value).toString()}%`;
      }
      columns[key.name] = isHidden(key.name, d) ? '' : percentageValue;
    }
    return columns;
  };

  const generateStringFields = (d: GetExcelRowDto) => {
    const columns: Record<string, string> = {};
    for (const key of EstimateItemFieldsDefinition.stringFields) {
      const value =
        (d[key.name as keyof GetExcelRowDto] as string) ?? undefined;
      columns[key.name] = isHidden(key.name, d) ? '' : value;
    }
    return columns;
  };

  const generateDictionaryFields = (d: GetExcelRowDto, lang: string) => {
    const columns: Record<string, React.ReactNode> = {};
    const suffix = lang === 'PL' ? 'ValuePl' : 'ValueEn';
    for (const key of EstimateItemFieldsDefinition.dictionaryFields) {
      const dictValue =
        (d[(key.name + suffix) as keyof GetExcelRowDto] as string) ?? undefined;
      const dictValuePl =
        (d[(key.name + 'ValuePl') as keyof GetExcelRowDto] as string) ??
        undefined;
      const dictValueEn =
        (d[(key.name + 'ValueEn') as keyof GetExcelRowDto] as string) ??
        undefined;
      const dictKey = d[key.name as keyof GetExcelRowDto] as string;
      const excelKeyName = `excel${key.name.charAt(0).toUpperCase()}${key.name.slice(1)}`;
      const excelValue =
        (d[excelKeyName as keyof GetExcelRowDto] as string) ?? undefined;
      const aggregatedRows = d.rowId.toString().split(',').length > 1;
      columns[key.name] = isHidden(key.name, d)
        ? ''
        : generateDictionaryFieldValue(
            d.rowId,
            key.name,
            aggregatedRows,
            dictValue,
            dictValuePl,
            dictValueEn,
            dictKey,
            excelValue,
            key.dictionaryName,
          );
    }
    return columns;
  };

  const generateBoolFields = (d: GetExcelRowDto) => {
    const columns: Record<string, string> = {};
    for (const key of EstimateItemFieldsDefinition.boolFields) {
      const value = d[key.name as keyof GetExcelRowDto] as boolean;
      columns[key.name] =
        value === undefined
          ? ''
          : value === true
            ? t(tk.common.yes)
            : t(tk.common.no);
    }
    return columns;
  };

  const isHidden = (key: string, d: GetExcelRowDto) => {
    return isFieldHidden(
      key,
      d.positionType as CreateUpdateEstimateItemDtoPositionTypeEnum,
      props.config.profile,
    );
  };

  const columns: GridColumn[] = [
    { header: t(tk.import.rowNumber), property: 'rowId' },
    { header: t(tk.common.status), property: 'status' },
  ];
  if (props.columns) {
    for (const column of props.columns) {
      if (column.columnName === 'projectNumber') {
        columns.push({
          header: t(tk.prLang.projectNumber),
          property: 'projectNumber',
          columnCssClass: 'noWrap',
        });
        continue;
      }
      if (
        column.columnName === 'month' ||
        column.columnName === 'startDate' ||
        column.columnName === 'endDate'
      ) {
        if (column.columnName !== 'endDate') {
          columns.push({
            header: t(tk.import.from),
            property: 'startDate',
            columnCssClass: 'noWrap',
          });
        }
        if (column.columnName !== 'startDate') {
          columns.push({
            header: t(tk.import.to),
            property: 'endDate',
            columnCssClass: 'noWrap',
          });
        }
        continue;
      }
      columns.push({
        header: lang === 'PL' ? column.columnNamePl : column.columnNameEn,
        property: column.columnName,
      });
    }
  }
  for (const column of props.otherRequiredColumns) {
    columns.push({
      header:
        (lang === 'PL' ? column.columnNamePl : column.columnNameEn) +
        t(tk.import.fromConfig),
      property: column.columnName,
    });
  }
  columns.push({ header: t(tk.import.errors), property: 'errors' });

  const data: GridRecord[] = [];
  for (const d of props.data) {
    if (props.hideErrors && d.status === ExcelImportStatusEnum.Error) {
      continue;
    }
    if (d.emptyRow) {
      continue;
    }
    const orderByNumberValues = getOrderByNumberColumns(
      d,
      EstimateItemFieldsDefinition.amountFieldsKeys.map(key => key.name),
    );
    const amountFields = generateAmountFields(d);
    const percentageFields = generatePercentageFields(d);
    const stringFields = generateStringFields(d);
    const dictionaryFields = generateDictionaryFields(d, lang);
    const boolFields = generateBoolFields(d);

    data.push({
      key: d.rowId,
      rowCssClass:
        d.status === ExcelImportStatusEnum.Error
          ? d.positionNotFoundError && d.excelPositionName && d.projectNumber
            ? 'info-row'
            : 'error-row'
          : d.status === ExcelImportStatusEnum.Duplicate
            ? 'warning-row'
            : '',
      values: {
        ...amountFields,
        ...percentageFields,
        ...stringFields,
        ...dictionaryFields,
        ...boolFields,
        rowId: d.rowId,
        status: t(
          ExcelImportStatusObj[d.status as keyof typeof ExcelImportStatusEnum],
        ),
        errors: generateErrorsInfo(d),
        positionName: generatePositionField(d),
        projectNumber: d.projectNumber ?? generateErrMsg(d.excelProjectNumber),
        startDate: d.startDate ? momencik(d.startDate, 'YYYY-MM-DD') : '',
        endDate: d.endDate ? momencik(d.endDate, 'YYYY-MM-DD') : '',
        invoiceInstructionAttachment: d.invoiceInstructionAttachment,
        specialistTeam: generateSpecialistTeamFieldValue(d),
      },
      orderByStringValues: {
        specialistTeam: getExcelRowTeamName(d) || d.excelSpecialistTeam || '',
      },
      orderByNumberValues: {
        ...orderByNumberValues,
        rowId: mathUtils.parse(d.rowId),
      },
    });
  }

  return (
    <Grid
      refresh={props.refresh}
      data={data}
      columns={columns}
      showFilter={false}
      availablePageSizes={[10, 20, 50, 100]}
      pageSize={100}
      emptyGridMessage={t(tk.import.noPositions)}
      trClassName="amounts-align-right font-90"
    />
  );
};
